import { debounce, flatMap, merge, set } from 'lodash-es';
import qs from 'qs';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useSelector } from '@/api/state';
import { FormCompleteActions } from '@/ui/pages/formPage/FormCompleteActions';
import { buildValidation } from '@/ui/pages/formPage/formUtils/buildValidation';
import { createValidatedField } from '@/ui/pages/formPage/formUtils/createValidatedField';
import { Button } from '@payaca/ambrosia';
import ContentPanel from '@payaca/components/contentPanel/ContentPanel';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import { getUrlSearchParam } from '@payaca/helpers/urlHelper';
import * as formActions from '@payaca/store/forms/formsActions';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { FormElement } from '@payaca/types/formElementTypes';
import { isNullish } from '@payaca/utilities/guards';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import './FormPage.sass';

const newFormState = {};

const getElemKeys = (elems: FormElement[], path: any[]): any => {
  return flatMap(elems, (elem: FormElement) => {
    if (elem.children) {
      switch (elem.type) {
        case 'multi-items': {
          let output: any[] = [];
          for (let i = 0; i < 10; i++) {
            output = [
              ...output,
              ...getElemKeys(elem.children || [], [...path, elem.id, i]),
            ];
          }
          return output;
        }
        default:
          return getElemKeys(elem.children || [], [...path, elem.id]);
      }
    }
    return [
      ...path.filter((i: any) => typeof i === 'number' || !!i),
      elem.id,
    ].join('.');
  });
};

const calculateValidity = (
  inputs: FormElement[],
  path: any[],
  validationState: any
) => {
  return inputs.reduce((acc: boolean, elem: FormElement) => {
    if (!acc) {
      return acc;
    }
    const a = getElemKeys(elem.children || [], [...path, elem.id]);
    const isInvalid = a.find((a: string) => {
      return validationState[a]?.isValid === false;
    });
    return !isInvalid;
  }, true);
};

const FormPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const params = useParams<{ formPreviewToken: string; pageIndex: string }>();
  const [showSavingMessage, setShowSavingMessage] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [selectedMenuSection, setSelectedMenuSection] = useState<
    number | undefined
  >(undefined);
  const [formProgressStr, setFormProgressStr] = useState('false');
  const [showExternalFormCompleteMessage, setShowExternalFormCompleteMessage] =
    useState(false);
  const [formCompleteData, setFormCompleteData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formTemplate = useSelector(
    (state) => state.forms.formData?.formTemplate
  );
  const formData = useSelector((state) => state.forms.formData?.formData);
  const [formState, setFormState] = useState(formData || newFormState);
  const initialFormState = useMemo(() => {
    return formData || newFormState;
  }, [formData]);
  const documentId = useSelector((state) => state.forms.formData?.documentId);
  const lastSavedTimestamp = useSelector((state) => state.forms.lastUpdated);

  const isLoading = useMemo(() => !formTemplate, [formTemplate]);
  const dealId = useMemo(
    () => getUrlSearchParam(location.search, 'dealId'),
    [location]
  );
  const isFromMobileApp = useMemo(
    () => !!getUrlSearchParam(location.search, 'isFromMobile'),
    [location]
  );

  const hasSavedCompleteFormData = useRef(false);

  const { update } = useIntercom();

  useEffect(() => {
    dispatch(formActions.requestGetAvailableForms());
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  useEffect(() => {
    update({
      hideDefaultLauncher: true,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'update'
  }, []);
  useEffect(() => {
    // setShowSavingMessage(false);
    setShowSavedMessage(true);
    setTimeout(() => {
      setShowSavedMessage(false);
    }, 3000);
  }, [lastSavedTimestamp]);

  useEffect(() => {
    if (!params?.pageIndex) {
      return;
    }
    setSelectedMenuSection(
      params.pageIndex === 'menu' ? undefined : +params.pageIndex
    );
  }, [params.pageIndex]);

  const formPreviewToken = useMemo(() => {
    return params.formPreviewToken;
  }, [params]);

  useEffect(() => {
    dispatch(formActions.clearFormData(formPreviewToken));
    dispatch(formActions.requestGetFormData(formPreviewToken));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [formPreviewToken]);

  const formStructure: FormElement[] = useMemo(() => {
    if (isNullish(selectedMenuSection)) return null;
    return formTemplate?.pages?.[selectedMenuSection]?.children || [];
  }, [formTemplate, selectedMenuSection]);

  const fieldValidators = useMemo(() => {
    return buildValidation(formTemplate?.pages || [], {}, [], formState);
  }, [formTemplate, formState]);

  const isSearchingForItems = useSelector(
    (state: any) => state.forms.isSearchingForItems
  );
  const searchItemResults = useSelector((state) => {
    return state.forms.searchItemResults;
  });

  const taskId = useMemo(() => {
    const query = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });
    return query?.taskId;
  }, [location]);

  const uiHandlers = useMemo(
    () => ({
      selectMenuSection: (index: number) =>
        history.push(`/forms/${formPreviewToken}/${index}${location.search}`),
    }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'history'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: isSearchingForItems
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: searchItemResults
    [formPreviewToken, location, searchItemResults, isSearchingForItems]
  );

  const handleFormSubmit = useCallback(() => {
    setIsSubmitting(true);
    setFormCompleteData({
      documentId,
      title: formTemplate.title,
    });
    dispatch(
      formActions.requestSubmitForm(formPreviewToken, () => {
        setIsSubmitting(false);
        if (!isFromMobileApp) {
          return history.push(
            // oxlint-disable-next-line typescript/no-base-to-string, typescript/restrict-template-expressions -- FIXME: pre-existing error: 'taskId' may use Object's default stringification format ('[object Object]') when stringified.
            `/documents/${documentId}${taskId ? `?taskId=${taskId}` : ''}`
          );
        }
        setShowExternalFormCompleteMessage(true);
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'isFromMobileApp', 'history', 'formTemplate.title', and 'dispatch'
  }, [formPreviewToken, documentId, taskId]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback received a function whose dependencies are unknown.
  const calculateFormCompleteness = useCallback(
    debounce(
      (validationState: any) => {
        const result = formTemplate?.pages?.reduce(
          (acc: any, page: any, index: number) => {
            acc[index] = calculateValidity(page.children, [], validationState);
            return acc;
          },
          {}
        );
        // TODO: move form progress inside the Validated form - this string prevents react state errors
        setFormProgressStr(JSON.stringify(result ?? false));
      },
      1000,
      {
        leading: true,
      }
    ),
    [formTemplate]
  );

  const formProgress = useMemo(() => {
    // TODO: move form progress inside the Validated form - this string prevents react state errors
    return JSON.parse(formProgressStr);
  }, [formProgressStr]);

  const numberOfCompleteSections = useMemo(() => {
    return formTemplate?.pages?.reduce(
      (acc: number, item: any, index: number) => {
        if (formProgress[index] && formData[`form_section_${index}_viewed`]) {
          return acc + 1;
        }
        return acc;
      },
      0
    );
  }, [formProgress, formTemplate, formData]);

  const performNavigation = useCallback(
    (newSectionIndex: number | 'menu') => {
      window.scrollTo(0, 0);
      history.push(
        `/forms/${formPreviewToken}/${newSectionIndex}${location.search}`
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: taskId
    [history, formPreviewToken, location, taskId]
  );

  const outstandingSectionsLabel = useMemo(() => {
    const sections = Object.values(formProgress).reduce(
      (acc: number[], sectionComplete: any, index: number) => {
        if (!sectionComplete) {
          return [...acc, index + 1];
        }
        return acc;
      },
      []
    );
    if (sections.length === 0) {
      return <MiniLoader />;
    }
    return `Missing answers in section${
      sections.length > 1
        ? `s ${sections.slice(0, -1).join(', ')} and ${
            sections[sections.length - 1]
          }`
        : ` ${sections?.[0]}`
    }`;
  }, [formProgress]);

  const saveFormData = useMemo(() => {
    let timeout: any;
    let batch: Array<any> = [];
    return (dataToSave: any, updatedFormState: any) => {
      if (timeout) {
        clearTimeout(timeout);
      }
      batch.push(dataToSave);
      timeout = setTimeout(() => {
        const mergedData = batch.reduce((acc: any, item: any) => {
          return merge(acc, item);
        }, {});
        batch = [];
        // setShowSavedMessage(false);
        setShowSavingMessage(true);
        if (!hasSavedCompleteFormData.current) {
          dispatch(
            formActions.requestUpdateFormData(
              formPreviewToken,
              updatedFormState,
              () => {
                hasSavedCompleteFormData.current = true;
                setShowSavingMessage(false);
                // setShowSavedMessage(true);
                // setFormState(updatedFormState);
              }
            )
          );
        } else {
          dispatch(
            formActions.requestUpdateFormData(
              formPreviewToken,
              mergedData,
              () => {
                hasSavedCompleteFormData.current = true;
                setShowSavingMessage(false);
                // setFormState(updatedFormState);
              }
            )
          );
        }
      }, 1000);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'dispatch', and 'formPreviewToken'
  }, []);

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }, callback?: any) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      const changeHandler = (value: { [key: string]: any }) => {
        onFieldChange(value, (updatedFormState: any) => {
          setFormState(updatedFormState);
          const fragmentToSave = { id: updatedFormState.id } as any;
          Object.keys(value).forEach((key) => {
            const first = key.split('.')[0];
            fragmentToSave[first] = updatedFormState[first];
            set(fragmentToSave, key, value[key]);
          });
          // triggerSavingNotification(setShowSavingMessage, setShowSavedMessage);
          saveFormData(fragmentToSave, updatedFormState);
        });
      };
      calculateFormCompleteness(validationState);

      let formTitle: ReactNode = '';
      if (typeof selectedMenuSection === 'number') {
        formTitle = (
          <>
            <UntitledIcon name={'chevron-left'} className="w-6 h-6" />
            <span>Overview</span>
          </>
        );
      } else if (!!dealId && !taskId) {
        formTitle = (
          <>
            <UntitledIcon name={'chevron-left'} className="w-6 h-6" />
            <span>Back to project</span>
          </>
        );
      } else if (taskId) {
        formTitle = (
          <>
            <UntitledIcon name={'chevron-left'} className="w-6 h-6" />
            <span>Back to tasks</span>
          </>
        );
      }

      return (
        <div>
          <div className={'form-page-header'}>
            {typeof selectedMenuSection === 'number' ||
            (!!dealId && !isFromMobileApp) ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  typeof selectedMenuSection === 'number'
                    ? performNavigation('menu')
                    : history.push(
                        !taskId ? `/projects/${dealId}/files` : `/tasks` // TODO: should use project publicId
                      )
                }
              >
                <span className={'form-title'}>{formTitle}</span>
              </Button>
            ) : (
              <div
                className={'mobile-menu-close-icon'}
                onClick={() => history.goBack()}
              >
                <UntitledIcon name="x.3" className="w-6 h-6" />
              </div>
            )}
            <div className={'form-page-header-rhs'}>
              <div className={'form-page-notifications'}>
                <div className={'form-page-notification'}>
                  {showSavingMessage && <MiniLoader />}
                  {showSavedMessage && (
                    <>
                      <UntitledIcon
                        name="check-square.3"
                        className="text-green-500"
                      />
                      <small> {`Progress saved!`}</small>
                    </>
                  )}
                </div>
              </div>
              <div className={'company-brand'}>
                {formData.form_header_company_logo && (
                  <img
                    className="logo"
                    src={formData.form_header_company_logo}
                  />
                )}{' '}
                {formData.form_header_company_name &&
                  !showSavingMessage &&
                  !showSavedMessage && (
                    <span>{formData.form_header_company_name}</span>
                  )}
              </div>
            </div>
          </div>
          <div className={'form-body'}>
            {typeof selectedMenuSection !== 'number' ? (
              <div className={'form-menu'}>
                <div className={'form-menu-header'}>
                  <div className={'form-menu-overview'}>
                    <h4>Overview</h4>
                    <p>{`All of the following sections must be completed before your final document can be generated. All progress is saved automatically.`}</p>
                  </div>
                  <div className={'progress-summary'}>
                    ({numberOfCompleteSections ?? 0}/
                    {formTemplate?.pages?.length ?? 0} Complete)
                  </div>
                </div>

                <ContentPanel className={'menu-element'}>
                  {formTemplate?.pages?.map((child: FormElement, i: number) => (
                    <div className={'menu-item-container'} key={i}>
                      <div
                        className={'menu-item'}
                        onClick={() => {
                          changeHandler({
                            [`form_section_${i}_viewed`]: true,
                          });
                          performNavigation(i);
                        }}
                      >
                        <div className={'menu-item-status'}>
                          <UntitledIcon
                            name="check-square.3"
                            className="text-green-500"
                          />
                        </div>
                        <div></div>
                        <div>
                          <div className={'menu-item-icon'}>
                            {child.icon ? (
                              <UntitledIcon
                                name={child.icon}
                                className="w-6 h-6"
                              />
                            ) : (
                              <UntitledIcon
                                name="edit-05.3"
                                className="w-6 h-6"
                              />
                            )}
                          </div>
                          <div className={'menu-item-title'}>
                            {child?.menuTitle}
                          </div>
                        </div>
                        <div className={'menu-item-label'}>Section {i + 1}</div>
                      </div>
                    </div>
                  ))}
                </ContentPanel>
                <div className={'form-footer'}>
                  <Button
                    onClick={() => !isSubmitting && handleFormSubmit()}
                    disabled={!isValid || showSavingMessage}
                    isProcessing={showSavingMessage || isSubmitting}
                    replaceContentWhenProcessing
                  >
                    {!isValid ? outstandingSectionsLabel : `View document`}
                  </Button>
                </div>
              </div>
            ) : (
              <div className={'form-page-section-container'}>
                <div className={'form-page-section-content'}>
                  <div className={'form-page-section-header'}>
                    <div>
                      {formTemplate?.pages?.[selectedMenuSection]?.title && (
                        <>
                          {formTemplate?.pages?.[selectedMenuSection]?.icon ? (
                            <UntitledIcon
                              name={
                                formTemplate?.pages?.[selectedMenuSection]
                                  ?.icon || 'edit-02.3'
                              }
                              className="w-6 h-6"
                            />
                          ) : (
                            <UntitledIcon name="edit-05.3" />
                          )}
                          <h4>
                            {formTemplate?.pages?.[selectedMenuSection]?.title}
                          </h4>
                        </>
                      )}
                    </div>
                    <div>
                      Section {(selectedMenuSection ?? 0) + 1}/
                      {formTemplate?.pages?.length ?? 0}
                    </div>
                  </div>
                  <div className={'form-sections'}>
                    {flatMap(
                      formStructure,
                      (formElement: FormElement, i: number) => {
                        if (
                          formElement.type === 'section' &&
                          formElement.children
                        ) {
                          return (
                            <div key={i} className={'form-page-section'}>
                              <div>
                                <div className={'form-page-section-title'}>
                                  {formElement.title && (
                                    <>
                                      <div
                                        className={
                                          'form-page-section-title-main'
                                        }
                                      >
                                        {formElement.icon ? (
                                          <UntitledIcon
                                            name={
                                              formTemplate?.pages?.[
                                                selectedMenuSection
                                              ]?.icon || 'edit-02.3'
                                            }
                                          />
                                        ) : (
                                          <UntitledIcon name="edit-05.3" />
                                        )}
                                        <h4>{formElement.title} </h4>
                                      </div>
                                      {!!formElement?.titleSuffix && (
                                        <span>{formElement.titleSuffix}</span>
                                      )}
                                    </>
                                  )}
                                </div>
                                {formElement?.description && (
                                  <p>{formElement.description}</p>
                                )}
                                {formElement?.children?.map(
                                  (elem: FormElement, index: number) => {
                                    return (
                                      <>
                                        {(elem.id ===
                                          'next_inspection_due_date' ||
                                          elem.id ===
                                            'add_next_inspection_calendar_event') &&
                                        (formState?.work_type ===
                                          'maintenance' ||
                                          formState?.work_type ===
                                            'call-out') ? (
                                          <div></div>
                                        ) : (
                                          <div key={elem.id}>
                                            {createValidatedField(
                                              elem,
                                              formState,
                                              formState[elem.id],
                                              changeHandler,
                                              onFieldTouch,
                                              touchedState,
                                              validationState,
                                              uiHandlers,
                                              index
                                            )}
                                          </div>
                                        )}
                                      </>
                                    );
                                  }
                                )}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={formElement.id}>
                            {createValidatedField(
                              formElement,
                              formState,
                              formState,
                              changeHandler,
                              onFieldTouch,
                              touchedState,
                              validationState,
                              uiHandlers,
                              i
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              </div>
            )}
            {typeof selectedMenuSection === 'number' && (
              <div className={'form-navigation-options'}>
                {selectedMenuSection !== 0 && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      performNavigation(selectedMenuSection - 1);
                      changeHandler({
                        [`form_section_${selectedMenuSection - 1}_viewed`]: true,
                      });
                    }}
                  >
                    Previous
                  </Button>
                )}
                {selectedMenuSection !==
                  (formTemplate?.pages?.length ?? 0) - 1 && (
                  <Button
                    onClick={() => {
                      performNavigation(selectedMenuSection + 1);
                      changeHandler({
                        [`form_section_${selectedMenuSection + 1}_viewed`]: true,
                      });
                    }}
                  >
                    Next section
                  </Button>
                )}
                {selectedMenuSection ===
                  (formTemplate?.pages?.length ?? 0) - 1 && (
                  <Button
                    onClick={() => !isSubmitting && handleFormSubmit()}
                    disabled={!isValid || showSavingMessage}
                    isProcessing={showSavingMessage || isSubmitting}
                    replaceContentWhenProcessing
                  >
                    {!isValid ? outstandingSectionsLabel : 'Submit'}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'formData.form_header_company_name', 'numberOfCompleteSections', 'formData.form_header_company_logo', 'saveFormData', 'isFromMobileApp', 'dealId', 'calculateFormCompleteness', 'selectedMenuSection', 'performNavigation', and 'history'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: formProgressStr
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: formPreviewToken
    [
      formPreviewToken,
      formTemplate,
      formStructure,
      formProgressStr,
      showSavingMessage,
      showSavedMessage,
      uiHandlers,
      outstandingSectionsLabel,
      taskId,
      isSubmitting,
      handleFormSubmit,
    ]
  );

  return (
    <ResponsiveViewWrapper
      className={'form-page-container'}
      downBreakpointSm={900}
    >
      {showExternalFormCompleteMessage ? (
        <FormCompleteActions
          isFromMobileApp={isFromMobileApp}
          // @ts-expect-error
          dealId={dealId}
          // @ts-expect-error
          documentData={formCompleteData}
        />
      ) : (
        <>
          {isLoading || !formTemplate ? (
            <LoaderOverlay />
          ) : (
            <ValidatedForm
              renderFormContents={renderFormContents}
              fieldValidators={fieldValidators}
              initialFormState={initialFormState}
            />
          )}
        </>
      )}
    </ResponsiveViewWrapper>
  );
};

export default FormPage;

import UntitledIcon from '@payaca/untitled-icons';
